import { CustomRangeConfig } from "../../codecs/custom-range-config.codec";
import { EdaPlacementBuilder } from "../lib";
import type { PublicRawMetricConfig } from "../raw-metrics.service";
import { CalculationType, OptimizationObjective, type PublicDimensionConfig } from "../types";

const RAW_METRICS = {
    gam_impressions: {},
    yield_impressions: {},
} as const satisfies Record<string, PublicRawMetricConfig>;
export type FlooringUPRDiscrepancyReportRawMetrics = keyof typeof RAW_METRICS;

const CALC_IDS = [
    "gam_impressions",
    "yield_impressions",
    "calculated_discrepancy",
    "calculated_discrepancy_percent",
] as const;
export type FlooringUPRDiscrepancyReportMetricsType = (typeof CALC_IDS)[number];

const DIMENSIONS = {
    flooring_upr_discrepancy_unified_pricing_rules: {
        label: "Unified Pricing Rule",
        row_type: "String",
    },
    flooring_upr_discrepancy_hash: {
        label: "AY Hash K/V",
        row_type: "Number",
    },
} as const satisfies Record<string, Omit<PublicDimensionConfig, "id">>;

export type FlooringUPRDiscrepancyReportDimensionsIds = keyof typeof DIMENSIONS;

export default new EdaPlacementBuilder()
    .withCustomRange(CustomRangeConfig.date)
    .withTimeRanges(["Last 7 Days", "Last 30 Days", "This Month", "Last Month"])
    .withTimeGranularities(["by_day", "by_week", "by_month"])
    .withRawMetrics(RAW_METRICS)
    .withCalculatedMetricsIds(CALC_IDS)
    .withCalculatedMetrics({
        calculated_discrepancy: {
            label: "Calculated Discrepancy",
            explanation: "Calculated Discrepancy",
            formula: m => m.gam_impressions - m.yield_impressions,
            calculationType: CalculationType.Sum,
            objective: OptimizationObjective.Minimize,
        },
        calculated_discrepancy_percent: {
            label: "Calculated Discrepancy Percentage",
            explanation: "Calculated Discrepancy Percentage",
            formula: m =>
                m.yield_impressions === null || m.yield_impressions === 0
                    ? 100
                    : (Math.abs(m.gam_impressions - m.yield_impressions) /
                          ((m.gam_impressions + m.yield_impressions) / 2)) *
                      100,
            calculationType: CalculationType.SpecialRate,
            decimalPlaces: 2,
            objective: OptimizationObjective.Minimize,
            keepFormulaFalsyAsFalsy: true,
        },
        gam_impressions: {
            label: "GAM Impressions",
            explanation: "GAM Impressions",
            formula: m => m.gam_impressions,
            calculationType: CalculationType.Sum,
            objective: OptimizationObjective.Maximize,
        },
        yield_impressions: {
            label: "Yield Impressions",
            explanation: "Yield Impressions",
            formula: m => m.yield_impressions,
            calculationType: CalculationType.Sum,
            objective: OptimizationObjective.Maximize,
        },
    })
    .withInitialMetrics(["gam_impressions", "yield_impressions"])
    .withMetricTable({
        tabMeta: {
            overall: {
                name: "Overall",
                explanation: "Automated discrepancy reporting on expected vs actual used unified pricing rules in GAM.",
            },
        },
        columnNames: {
            impressions: "Impressions",
        },
        tableMapping: {
            overall: {
                impressions: [
                    "gam_impressions",
                    "yield_impressions",
                    "calculated_discrepancy",
                    "calculated_discrepancy_percent",
                ],
            },
        },
    })
    .withDimensions(DIMENSIONS);
